import {
  Box,
  Button,
  HStack,
  VStack,
  Center,
  Flex,
  Spinner,
  Text,
  ListItem,
  UnorderedList,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { plural } from '@lingui/core/macro';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useSnapshot } from 'valtio';
import {
  CourseReferralModalMode,
  courseReferralModalState,
} from '../../tenantState/CourseReferralState';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { StudentScheduleItem as StudentScheduleItemType } from '@duke-hq/svc-api/student/services/schedulerService';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { useMemo } from 'react';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { overflowScrollbarStyles } from '@duke-hq/ui/styles/overflowScrollbarStyles';
import { CompletionBadge } from './CompletionBadge';

type ReviewCourseProps = {
  goToNextStep: () => void;
  goToPrevStep: () => void;
};

export function ReviewCourse({ goToNextStep, goToPrevStep }: ReviewCourseProps) {
  const modalState = useSnapshot(courseReferralModalState);

  const { data: courseData, isLoading } = trpcTenant.courses.byId.useQuery(
    modalState.selectedCourseAttributes.id,
    { enabled: !!modalState.selectedCourseAttributes.id },
  );

  const { data: completionStatusData } = trpcTenant.enrolments.getCompletionStatus.useQuery(
    {
      courseEnrolmentId: Number(modalState.enrolmentSettingsData.enrolmentId),
    },
    {
      enabled:
        modalState.enrolmentSettingsData.enrolmentId !== null &&
        modalState.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER,
    },
  );

  if (!modalState.selectedCourseAttributes.id) {
    return (
      <FormMainContainer>
        <FormContentContainer>
          <Box w="full" textAlign="left">
            <Text color="fg.muted" textStyle="sm">
              <Trans>No course is selected. Please select one to continue.</Trans>
            </Text>
          </Box>
        </FormContentContainer>
        <FormFooterContainer>
          <div />
          <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
            <Trans>Back</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    );
  }

  return (
    <FormMainContainer>
      <FormContentContainer>
        {isLoading ? (
          <>
            <VStack w="full" justify="start" align="start" gap="6px">
              {modalState.selectedCourseAttributes.identifier ? (
                <Text fontSize="md" lineHeight="6" color="fg.muted">
                  {modalState.selectedCourseAttributes.identifier}
                </Text>
              ) : null}
              <Box overflow="hidden" alignSelf="stretch">
                <Text fontSize="2xl" lineHeight="8" color="fg.default" fontWeight="bold">
                  {modalState.selectedCourseAttributes.title}
                </Text>
              </Box>
            </VStack>
            <Center h="full" w="full">
              <Spinner color="border.accent" size="lg" />
            </Center>
          </>
        ) : (
          <CourseReview
            data={courseData}
            title={modalState.selectedCourseAttributes.title}
            identifier={modalState.selectedCourseAttributes.identifier}
            completionStatus={completionStatusData}
          />
        )}
      </FormContentContainer>
      <FormFooterContainer>
        <Button rightIcon={<ChevronRightIcon />} onClick={goToNextStep}>
          <Trans>Next</Trans>
        </Button>
        {modalState.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER ? null : (
          <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
            <Trans>Back</Trans>
          </Button>
        )}
      </FormFooterContainer>
    </FormMainContainer>
  );
}

interface CourseReviewProps {
  data: RouterOutputs['courses']['byId'] | undefined;
  title: string;
  identifier: string;
  completionStatus?: RouterOutputs['enrolments']['getCompletionStatus'];
}

function CourseUnitOfCompetency({ unit }: { unit: StudentScheduleItemType }) {
  return (
    <Flex
      align="start"
      justify="center"
      direction="column"
      border="1px solid"
      borderColor="border.default"
      borderRadius="8px"
      padding="16px"
      bg="bg.subtle"
      w="full"
    >
      {unit?.['Unit Code'] ? (
        <Text color="fg.muted" fontSize="sm">
          {unit['Unit Code']}
        </Text>
      ) : null}
      <VStack align="start" justify="start" spacing="4px">
        <Text color="fg.emphasis" textStyle="md" fontWeight="semibold">
          {unit.Title}
        </Text>
      </VStack>
    </Flex>
  );
}

function SemesterSection({
  semester,
  units,
  isDefaultOpen,
  isCompleted,
}: {
  semester: string;
  units: StudentScheduleItemType[];
  isDefaultOpen: boolean;
  isCompleted: boolean | null;
}) {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isDefaultOpen });

  if (units == null || units.length === 0) {
    return null;
  }

  return (
    <VStack w="full" align="start" spacing="12px">
      <HStack align="center" spacing="0">
        <Button
          variant="ghost"
          onClick={onToggle}
          leftIcon={isOpen ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
          size="sm"
          color="fg.emphasis"
          fontWeight="semibold"
          p={0}
          h="auto"
          minH="auto"
        >
          {semester === '0' ? (
            <Trans>Language, Literacy and Numeracy</Trans>
          ) : (
            <Trans>
              Semester {semester} (
              {plural(units.length, {
                one: '# unit',
                other: '# units',
              })}
              )
            </Trans>
          )}
        </Button>
        {isCompleted != null ? <CompletionBadge isCompleted={isCompleted} /> : null}
      </HStack>

      <Collapse in={isOpen} animateOpacity style={{ width: '100%' }}>
        <UnorderedList listStyleType="none" p={0} m={0} w="full" spacing="0">
          {units.map((item, index: number) => (
            <ListItem key={item.id} w="full">
              <VStack w="full" align="center" gap="0">
                <CourseUnitOfCompetency unit={item} />
                {index !== units.length - 1 && <Box bg="border.emphasis" w="2px" h="16px" />}
              </VStack>
            </ListItem>
          ))}
        </UnorderedList>
      </Collapse>
    </VStack>
  );
}

export function CourseReview({ data, title, identifier, completionStatus }: CourseReviewProps) {
  const courseReferralModalSnap = useSnapshot(courseReferralModalState);

  const getSemesterCompletionStatus = (semester: string): boolean | null => {
    if (!completionStatus) {
      return null;
    }

    // Show completion badge only for semester 1 and LLN
    switch (semester) {
      case '0':
        return completionStatus.isLLNComplete;
      case '1':
        return completionStatus.isSemester1Complete;
      default:
        return null;
    }
  };
  const { hasWorkPlacement, schedulerDataBySemester, shouldShowCollapsible } = useMemo(() => {
    // Check if any resource has work_placement set to true
    const hasWorkPlacement = data?.courseItems.some(
      (item) => item.unitOfCompetency?.work_placement === true,
    );

    const allUnits =
      Array.isArray(data?.schedule) && data?.schedule?.length
        ? data.schedule.filter((item) => item.type === 'UNIT')
        : [];

    // Group units by semester
    const schedulerDataBySemester = allUnits.reduce(
      (acc, item) => {
        const semester = item.Semester || '1';
        if (!acc[semester]) {
          acc[semester] = [];
        }
        acc[semester].push(item);
        return acc;
      },
      {} as Record<string, typeof allUnits>,
    );

    const semesterCount = Object.keys(schedulerDataBySemester).filter(
      // Only count non-LLN semesters
      (key) => key !== '0',
    ).length;

    // Show collapsible only if: add_second_semester mode and more than one semester
    const shouldShowCollapsible =
      courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER &&
      semesterCount > 1;

    return {
      hasWorkPlacement,
      schedulerDataBySemester,
      shouldShowCollapsible,
    };
  }, [data, courseReferralModalSnap.mode]);

  useMemo(() => {
    if (data && courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER) {
      courseReferralModalState.setSelectedCourseAttributes({
        id: data.id,
        identifier: data.identifier ?? '',
        price_aud: Number(data.price_aud) / 2,
        specialisation: data.specialisation ?? '',
        title: data.title,
        kind: CourseKindSchema.parse(data.kind),
        status: data.status,
      });
    }
  }, [data, courseReferralModalSnap.mode]);

  if (!data) {
    return (
      <VStack w="full" h="full" justify="start" align="start" gap="10px">
        <VStack w="full" justify="start" align="start" gap="6px">
          {identifier ? (
            <Text fontSize="md" lineHeight="6" color="fg.muted">
              {identifier}
            </Text>
          ) : null}
          <Box overflow="hidden" alignSelf="stretch">
            <Text fontSize="2xl" lineHeight="8" color="fg.default" fontWeight="bold">
              {title}
            </Text>
          </Box>
        </VStack>
        <Text color="fg.muted" textStyle="sm">
          <Trans>Sorry, we couldn't load the course data. Please refresh or try again later.</Trans>
        </Text>
      </VStack>
    );
  }

  return (
    <VStack w="full" h="full" justify="start" align="start" gap="10px">
      <VStack w="full" justify="start" align="start" gap="6px">
        {courseReferralModalSnap.selectedCourseAttributes.identifier ? (
          <Text fontSize="md" lineHeight="6" color="fg.muted">
            {courseReferralModalSnap.selectedCourseAttributes.identifier}
          </Text>
        ) : null}
        <Box overflow="hidden" alignSelf="stretch">
          <Text fontSize="2xl" lineHeight="8" color="fg.default" fontWeight="bold">
            {courseReferralModalSnap.selectedCourseAttributes.kind ===
            CourseKindSchema.enum.SKILL_SET
              ? `Skill set: ${courseReferralModalSnap.selectedCourseAttributes.title}`
              : courseReferralModalSnap.selectedCourseAttributes.title}
          </Text>
        </Box>
        <HStack gap="8px" justify="start" align="center">
          <HStack gap="4px" justify="start" align="center">
            <Text fontSize="sm" lineHeight="5" color="fg.muted">
              <Trans>Cost</Trans>:
            </Text>
            <Text fontSize="sm" lineHeight="5" color="fg.default" fontWeight="medium">
              ${courseReferralModalSnap.selectedCourseAttributes.price_aud.toString()}
            </Text>
          </HStack>
          {hasWorkPlacement === true ? (
            <>
              <Text fontSize="sm" lineHeight="5" color="fg.subtle">
                •
              </Text>
              <Text fontSize="sm" lineHeight="5" color="warning.deep" fontWeight="medium">
                <Trans>Requires work placement</Trans>
              </Text>
            </>
          ) : null}
        </HStack>
      </VStack>

      <Box w="full" h="full" minH="0" display="flex" flexDirection="column">
        {data.description != null ? (
          <Text textStyle="sm" color="fg.muted" mb={4} flexShrink={0}>
            {data.description}
          </Text>
        ) : null}

        {shouldShowCollapsible ? (
          <VStack
            overflowY="auto"
            overflowX="hidden"
            minHeight="100px"
            maxHeight="400px"
            flex={1}
            w="full"
            pr={2}
            align="start"
            spacing="24px"
            css={overflowScrollbarStyles}
          >
            {Object.keys(schedulerDataBySemester).length > 0
              ? Object.entries(schedulerDataBySemester)
                  .sort(([a], [b]) => parseInt(a) - parseInt(b))
                  .map(([semester, units]) => (
                    <SemesterSection
                      key={semester}
                      semester={semester}
                      units={units}
                      isDefaultOpen={
                        courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER
                          ? semester === '2'
                          : true
                      }
                      isCompleted={getSemesterCompletionStatus(semester)}
                    />
                  ))
              : null}
          </VStack>
        ) : (
          <UnorderedList
            listStyleType="none"
            overflowY="auto"
            overflowX="hidden"
            minHeight="100px"
            maxHeight="400px"
            flex={1}
            p={0}
            m={0}
            w="full"
            pr={2}
            css={overflowScrollbarStyles}
          >
            {Object.keys(schedulerDataBySemester).length > 0
              ? Object.entries(schedulerDataBySemester)
                  .sort(([a], [b]) => parseInt(a) - parseInt(b))
                  .flatMap(([, units]) => units)
                  .map((item, index, allUnits) => (
                    <ListItem key={item.id} w="full">
                      <VStack w="full" align="center" gap="0">
                        <CourseUnitOfCompetency unit={item} />
                        {index !== allUnits.length - 1 && (
                          <Box bg="border.emphasis" w="2px" h="16px" />
                        )}
                      </VStack>
                    </ListItem>
                  ))
              : null}
          </UnorderedList>
        )}
      </Box>
    </VStack>
  );
}
